import React from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { Table, TableContainer, TableHead, TableRow, TableCell, TableBody, TextField, Paper, InputAdornment } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import variables from 'src/style/variable/variables.module.scss';
import { useTranslation } from 'src/i18n';
import 'src/screen/Protected/DeviceScreen/TestingReport/TestingReport.scss';
import { pairUp } from './utils';
import DeviceTagAutocompleteField from './DeviceTagAutocompleteField';

/**
 * The Project Information section every field report opens with: a two-column grid of
 * field/value pairs written to `extras.projectInfo`.
 *
 * Labels are resolved as `<i18nPrefix>.table.<field>`, so a report supplies its own field list
 * and prefix rather than its own copy of this table.
 *
 * One field is the device tag, which renders as the autocomplete that links the report.
 *
 * `sectionKey` and `labelPath` repoint it at another extras section for any other flat
 * field/value block — e.g. the EHT circuit audit's electrical readings. Omit `tagField` there,
 * since only the project info section carries the device link. `sectionPath` takes an array
 * instead, for a section nested under a list — a sub-circuit's readings, say.
 *
 * A field listed in `units` renders its unit inside the input as an end adornment, so the
 * label stays clean — the same treatment as the cable insulation measurements. It is numeric
 * too, since a unit implies a measured reading.
 */
export default function ProjectInfoTable (props) {
  const {
    fields, i18nPrefix, tagField, deviceType, sectionKey, sectionPath, labelPath, titleKey,
    titleVars, units, soloFields, data, onChange, readOnly, linkedDeviceTag, onUnlink,
    onDeviceSelect,
  } = props;
  const { t } = useTranslation();
  const isMobile = useMediaQuery(`(max-width: ${variables.mobileWidth})`);

  const path = sectionPath || [sectionKey];
  const projectInfo = data?.getIn(path);

  // A solo field takes a row alone — the cells beside it stay empty — ahead of the rest,
  // which pair up two per row.
  const isSolo = (field: string) => soloFields.includes(field);
  const rows: [string, string | null][] = [
    ...fields.filter(isSolo).map((field): [string, null] => [field, null]),
    ...pairUp(fields.filter((field: string) => !isSolo(field))),
  ];

  const onFieldChange = (key, val) => {
    onChange(['extras', ...path, key], val);
  };

  const renderInfoField = (field) => {
    if (field === tagField) {
      return (
        <DeviceTagAutocompleteField
          value={projectInfo?.get(field) || ''}
          disabled={readOnly}
          deviceType={deviceType}
          placeholder={t(`${i18nPrefix}.${labelPath}.${field}`)}
          linkedDeviceTag={linkedDeviceTag}
          onChange={(val) => onFieldChange(field, val)}
          onDeviceSelect={onDeviceSelect}
          onUnlink={onUnlink}
        />
      );
    }
    const unit = units[field];
    return (
      <TextField
        fullWidth
        disabled={readOnly}
        type={unit ? 'number' : 'text'}
        value={projectInfo?.get(field) || ''}
        onChange={(e) => onFieldChange(field, e.target.value)}
        InputProps={unit ? { endAdornment: <InputAdornment position='end'>{unit}</InputAdornment> } : undefined}
      />
    );
  };

  return (
    <div>
      <Paper className='field-report-section'>
        <h3>{t(`${i18nPrefix}.${titleKey}`, titleVars)}</h3>
        <TableContainer className={`default-control-panel-table${isMobile ? ' mobile-table' : ''}`}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className='main-headers'>{t('common.field')}</TableCell>
                <TableCell className='main-headers'>{t('common.value')}</TableCell>
                <TableCell className='main-headers'>{t('common.field')}</TableCell>
                <TableCell className='main-headers'>{t('common.value')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(([left, right]) => (
                <TableRow key={left}>
                  <TableCell className='sub-headers'>{t(`${i18nPrefix}.${labelPath}.${left}`)}</TableCell>
                  <TableCell className='body-cell'>{renderInfoField(left)}</TableCell>
                  {right ? (
                    <>
                      <TableCell className='sub-headers'>{t(`${i18nPrefix}.${labelPath}.${right}`)}</TableCell>
                      <TableCell className='body-cell'>{renderInfoField(right)}</TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell />
                      <TableCell />
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </div>
  );
}

ProjectInfoTable.propTypes = {
  // Field keys in the paper form's reading order; pairUp lays them out two pairs per row.
  fields: PropTypes.arrayOf(PropTypes.string).isRequired,
  // Namespace for this report's labels, e.g. 'panelAudit'.
  i18nPrefix: PropTypes.string.isRequired,
  // The field holding the device tag. Omit for sections that carry no device link.
  tagField: PropTypes.string,
  deviceType: PropTypes.string,
  // Section under `extras` this table reads and writes.
  sectionKey: PropTypes.string,
  // Full path under `extras`, for a section nested under a list. Overrides sectionKey.
  sectionPath: PropTypes.array,
  // Middle segment of the label keys: `<i18nPrefix>.<labelPath>.<field>`.
  labelPath: PropTypes.string,
  // Heading key, resolved as `<i18nPrefix>.<titleKey>`.
  titleKey: PropTypes.string,
  // Interpolation values for the heading, e.g. { subCircuitIdx: '1' }.
  titleVars: PropTypes.object,
  // Unit per field, e.g. { voltageAtPowerBox: 'VAC' }, shown inside the input. A field with a
  // unit takes a number.
  units: PropTypes.objectOf(PropTypes.string),
  // Fields that take a row to themselves rather than pairing up, e.g. a block's leading tag.
  soloFields: PropTypes.arrayOf(PropTypes.string),
  data: ImmutablePropTypes.map,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  linkedDeviceTag: PropTypes.string,
  onUnlink: PropTypes.func,
  onDeviceSelect: PropTypes.func,
};

ProjectInfoTable.defaultProps = {
  deviceType: 'controller',
  tagField: null,
  sectionKey: 'projectInfo',
  sectionPath: null,
  labelPath: 'table',
  titleKey: 'projectInformation',
  titleVars: undefined,
  units: {},
  soloFields: [],
  readOnly: false,
  linkedDeviceTag: null,
  onUnlink: () => {},
  onDeviceSelect: null,
};
